import * as React from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import ArrowForwardOutlinedIcon from "@mui/icons-material/ArrowForwardOutlined";
import { Box } from "@mui/system";
import { CircularProgress, Step, StepButton, Stepper } from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ProcessInfo from "./UpdateBrandDataSteps/ProcessInfo";
import ProcessSetting from "./UpdateBrandDataSteps/ProcessSetting";
import ReviewAndRun from "./UpdateBrandDataSteps/Review&Run";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import { useSelector } from "react-redux";
import {
  setEndDate,
  setIsLoading,
  setIsSuccess,
  setModalOpen,
  setSelectedAction,
  setSelectedActionId,
  setSelectedBrandConfigs,
  setSelectedBusinessAdvisorDestinationTableId,
  setSelectedFlowDestinationTableId,
  setSelectedProcessActions,
  setSelectedProcessSettingMode,
  setSelectedUnifierDestinationTableId,
  setStartDate,
} from "../../../../features/dataCollectionBrandsSlice";
import { useDispatch } from "react-redux";
import { selectUserToken } from "../../../../features/userSlice";
import { dataCollectionBrandsEndPointLogic } from "../../../../logic/DataCollection/Brands/BrandsEndPointLogic";
import dayjs from "dayjs";
import {
  actionIdMapping,
  APPLICABLE_INDUSTRY_IDS,
  INDUSTRY_ADVISOR_TABLE_IDS,
  INDUSTRY_UNIFIER_TABLE_IDS,
} from "./actionIds";
interface Props {
  open: boolean;
}

const fetcherFlowConfigs = [
  { configName: "Brand Performance", destinationKey: "Daily" },
  { configName: "User Performance", destinationKey: "User" },
  { configName: "Monthly Brand Performance", destinationKey: "Monthly" },
];

const unifierFlowConfigs = [
  { configName: "Brand Performance", destinationTableIds: [2] },
  { configName: "User Performance", destinationTableIds: [4] },
  { configName: "Monthly Brand Performance", destinationTableIds: [1] },
];

const advisorFlowConfigs = [
  { configName: "Brand Performance", destinationTableIds: [2] },
  { configName: "User Performance", destinationTableIds: [3] },
  { configName: "Monthly Brand Performance", destinationTableIds: [1] },
];

export default function UpdateBrandData({ open }: Props) {
  const {
    selectedAction,
    selectedBrandConfigs,
    selectedActionId,
    selectedProcessActions,
    selectedProcessSettingMode,
    selectedFlowDestinationTableId,
    brandDataInModal,
    startDate,
    endDate,
    isLoading,
    destinationTableData,
  } = useSelector((state: any) => state.dataCollectionBrands);
  const oauthToken: any = useSelector(selectUserToken);
  const dispatch = useDispatch();
  const steps = ["Process Info", "Process Settings", "Review & Run"];
  const [activeStep, setActiveStep] = React.useState(0);
  const [completed, setCompleted] = React.useState<{ [k: number]: boolean }>(
    {},
  );
  const [isProcessInfoDateValid, setIsProcessInfoDateValid] =
    React.useState(true);
  const hasError = false; // Define hasError variable Plese use usestate here when develop
  const processActions = selectedProcessActions || [];
  const hasProcessAction = (action: string) => processActions.includes(action);
  const isInvalidActionCombination =
    hasProcessAction("Fetcher") &&
    ((hasProcessAction("Unifier") && !hasProcessAction("Advisor")) ||
      (hasProcessAction("Advisor") && !hasProcessAction("Unifier")));
  const isEndToEndSelection =
    hasProcessAction("Fetcher") &&
    hasProcessAction("Unifier") &&
    hasProcessAction("Advisor");
  const isFetcherByReport =
    selectedAction === "Fetcher" && selectedProcessSettingMode === "By Report";
  // All brands in the modal belong to an applicable industry (21/22). Mixed
  // industry selections are blocked earlier (on the "Run Processes" button).
  const isApplicableIndustry =
    Array.isArray(brandDataInModal) &&
    brandDataInModal.length > 0 &&
    brandDataInModal.every((b: any) =>
      APPLICABLE_INDUSTRY_IDS.includes(Number(b.industry_id)),
    );
  // Unifier / Advisor / Unifier + Advisor for applicable industries skip the
  const isIndustryShortcutFlow =
    isApplicableIndustry &&
    (selectedAction === "Unifier" ||
      selectedAction === "business_advisor" ||
      selectedAction === "unifier_business_advisor");
  // Fetcher for applicable industries also skips the settings step and runs
  // with every "By Report" table id.
  const isFetcherIndustryShortcut =
    isApplicableIndustry && selectedAction === "Fetcher";
  const shouldSkipProcessSettings =
    isEndToEndSelection || isIndustryShortcutFlow || isFetcherIndustryShortcut;
  const handleClose = () => {
    dispatch(setModalOpen({ isOpen: false, data: {} }));
    dispatch(setSelectedAction(null));
    dispatch(setSelectedProcessActions([]));
    dispatch(setSelectedProcessSettingMode("By Flow"));
    dispatch(setSelectedActionId(null));
    setActiveStep(0);
    setCompleted({});
    dispatch(setSelectedBrandConfigs([]));
    dispatch(setSelectedUnifierDestinationTableId([]));
    dispatch(setSelectedBusinessAdvisorDestinationTableId([]));
    dispatch(setSelectedFlowDestinationTableId([]));
    dispatch(setStartDate(dayjs().format("YYYY-MM-DD")));
    dispatch(setEndDate(dayjs().format("YYYY-MM-DD")));
    setIsProcessInfoDateValid(true);
  };

  // Mirrors the "By Report" options built in the Process Settings step: every
  // report (config name + table id) available for each brand in the modal.
  const getAllReportSelections = () => {
    return (brandDataInModal || [])
      .map((brand: any) => ({
        brandId: brand.brandId,
        brandName: brand.brandName,
        selectedConfigs: (brand.row_output_table_data || []).map(
          (tableData: any) => ({
            configName: tableData.config_name,
            tableId: tableData.table_id,
          }),
        ),
      }))
      .filter((brand: any) => brand.selectedConfigs.length > 0);
  };

  const handleNext = () => {
    if (activeStep === 0 && !isProcessInfoDateValid) {
      return;
    }

    // Always mark current step as completed
    setCompleted((prev) => ({ ...prev, [activeStep]: true }));
    if (shouldSkipProcessSettings) {
      if (isFetcherIndustryShortcut) {
        // Settings step is skipped, so pre-select every "By Report" option.
        dispatch(setSelectedProcessSettingMode("By Report"));
        dispatch(setSelectedBrandConfigs(getAllReportSelections()));
      } else if (isIndustryShortcutFlow) {
        // Clear any report selection left over from a previous Fetcher run.
        dispatch(setSelectedProcessSettingMode("By Flow"));
        dispatch(setSelectedBrandConfigs([]));
      }
      setActiveStep(2); // Skip to Review & Run
    } else {
      setActiveStep((prev) => prev + 1);
    }
  };

  const isNextDisabled =
    activeStep === steps.indexOf("Process Settings") &&
    ((isFetcherByReport && selectedBrandConfigs.length === 0) ||
      (!isFetcherByReport && selectedFlowDestinationTableId?.length === 0));

  const getActionFromSelections = (actions: string[]) => {
    const hasFetcher = actions.includes("Fetcher");
    const hasUnifier = actions.includes("Unifier");
    const hasAdvisor = actions.includes("Advisor");
    if (hasFetcher && hasUnifier && hasAdvisor) return "End_to_end_sync";
    if (hasFetcher && !hasUnifier && !hasAdvisor) return "Fetcher";
    if (!hasFetcher && hasUnifier && !hasAdvisor) return "Unifier";
    if (!hasFetcher && !hasUnifier && hasAdvisor) return "business_advisor";
    if (!hasFetcher && hasUnifier && hasAdvisor) {
      return "unifier_business_advisor";
    }
    return null;
  };

  const handleActionSelect = (actions: string[]) => {
    const action = getActionFromSelections(actions);
    dispatch(setSelectedAction(action));
    const env =
      import.meta.env.VITE_ENV === "production" ? "production" : "staging";
    const id = action ? actionIdMapping[env][action] : null;
    dispatch(setSelectedActionId(id));
  };

  const handleBack = () => {
    if (activeStep === 2 && shouldSkipProcessSettings) {
      // If coming from skipped step, go back to step 0
      setActiveStep(0);
      setCompleted((prev) => {
        const newCompleted = { ...prev };
        delete newCompleted[0]; // Unmark step 0 completion
        return newCompleted;
      });
    } else {
      setActiveStep((prev) => prev - 1);
      setCompleted((prev) => {
        const newCompleted = { ...prev };
        delete newCompleted[activeStep - 1]; // Unmark previous step
        return newCompleted;
      });
    }
  };

  const handleStep = (step: number) => {
    if (step > 0 && !isProcessInfoDateValid) {
      return;
    }

    if (selectedAction) {
      setActiveStep(step);
    }
  };

  const getUniqueValues = (values: any[]) => {
    return Array.from(new Set(values.filter(Boolean)));
  };

  const getDestinationTableIds = (brandId: string, destinationKey: string) => {
    return (destinationTableData || [])
      .filter((item: any) => {
        const destinationTables = String(
          item.destination_tables || "",
        ).toLowerCase();
        return (
          String(item.brand_id) === String(brandId) &&
          destinationTables.includes(destinationKey.toLowerCase())
        );
      })
      .map((item: any) => item.raw_output_table_name)
      .filter(Boolean);
  };

  const getFlowConfigs = () => {
    if (selectedAction === "Unifier") {
      return unifierFlowConfigs;
    }
    if (
      selectedAction === "business_advisor" ||
      selectedAction === "unifier_business_advisor"
    ) {
      return advisorFlowConfigs;
    }
    return fetcherFlowConfigs;
  };

  const getAllFlowOptions = () => {
    return (brandDataInModal || []).flatMap((brand: any) =>
      getFlowConfigs()
        .map((config: any) => {
          const destinationTableIds =
            config.destinationTableIds ||
            getDestinationTableIds(brand.brandId, config.destinationKey);
          return {
            brandId: brand.brandId,
            configName: config.configName,
            destinationTableIds,
          };
        })
        .filter((option: any) => option.destinationTableIds.length > 0),
    );
  };

  const hasEveryOptionSelected = (
    allOptions: any[],
    selectedOptions: any[],
    getKey: (option: any) => string,
  ) => {
    if (allOptions.length === 0) return false;
    const selectedKeys = new Set(selectedOptions.map(getKey));
    return allOptions.every((option: any) => selectedKeys.has(getKey(option)));
  };

  /**
   * Builds and submits the Run Process payload. Always includes
   * process_id, start_date, end_date; the rest depends on the flow:
   *
   *  • End-to-End Sync        → brand_id only
   *  • Fetcher (By Report)    → table_id (selected report tableIds)
   *  • Fetcher (By Flow)      → table_id (selected flow ids)
   *  • Fetcher, Industry 21/22 (settings skipped)
   *                           → table_id (every "By Report" tableId)
   *
   *  • Unifier / Advisor / Unifier+Advisor → always brand_id, plus:
   *      - Industry 21/22 (settings skipped): fixed ids →
   *          Unifier         → unifier_table_id: [5]
   *          Advisor         → advisor_table_id: [4]
   *          Unifier+Advisor → unifier_table_id: [5], advisor_table_id: [4]
   *      - Other industry, partial selection: selected ids →
   *          Unifier         → unifier_table_id
   *          Advisor         → advisor_table_id
   *          Unifier+Advisor → same ids under both keys
   *      - Other industry, all selected: brand_id only (no table ids)
   */

  const handleFinish = async () => {
    try {
      dispatch(setIsLoading(true));
      let payload: any = {
        process_id: selectedActionId,
        start_date: startDate,
        end_date: endDate,
      };
      if (selectedActionId === 196 || selectedActionId === 100) {
        // End-to-End Sync: use brandId
        const brandIds = brandDataInModal?.map((b: any) => b.brandId);
        payload = { ...payload, brand_id: brandIds };
      } else if (
        (selectedActionId === 190 || selectedActionId === 97) &&
        (selectedProcessSettingMode === "By Report" ||
          isFetcherIndustryShortcut)
      ) {
        //   const brandIds = getUniqueValues(
        //   (brandDataInModal || []).map((brand: any) => brand.brandId),
        // );
        // Industry 21/22 skips the settings step, so take every report option.
        const reportSelections = isFetcherIndustryShortcut
          ? getAllReportSelections()
          : selectedBrandConfigs;
        const tableIds = reportSelections?.flatMap((brand: any) =>
          brand.selectedConfigs?.map((cfg: any) => cfg.tableId),
        );
        // payload = { ...payload, brand_id: brandIds };
        // Always send table_id for Fetcher, regardless of whether all reports are selected.
        payload = { ...payload, table_id: getUniqueValues(tableIds) };
      } else {
        const destinationTableIds = Array.from(
          new Set(
            selectedFlowDestinationTableId?.flatMap(
              (cfg: any) => cfg.destinationTableIds || [],
            ),
          ),
        );
        const brandIds = getUniqueValues(
          (brandDataInModal || []).map((brand: any) => brand.brandId),
        );
        const allFlowOptions = getAllFlowOptions();
        const areAllFlowOptionsSelected = hasEveryOptionSelected(
          allFlowOptions,
          selectedFlowDestinationTableId || [],
          (option: any) => `${option.brandId}-${option.configName}`,
        );
        const idKey =
          selectedActionId === 190 || selectedActionId === 97
            ? "table_id"
            : "destination_table_id";
        if (idKey === "destination_table_id") {
          payload = { ...payload, brand_id: brandIds };
          if (isApplicableIndustry) {
            if (selectedAction === "Unifier") {
              payload = {
                ...payload,
                unifier_table_id: INDUSTRY_UNIFIER_TABLE_IDS,
              };
            } else if (selectedAction === "business_advisor") {
              payload = {
                ...payload,
                advisor_table_id: INDUSTRY_ADVISOR_TABLE_IDS,
              };
            } else if (selectedAction === "unifier_business_advisor") {
              payload = {
                ...payload,
                unifier_table_id: INDUSTRY_UNIFIER_TABLE_IDS,
                advisor_table_id: INDUSTRY_ADVISOR_TABLE_IDS,
              };
            }
          } else if (!areAllFlowOptionsSelected) {
            if (selectedAction === "Unifier") {
              payload = { ...payload, unifier_table_id: destinationTableIds };
            } else if (selectedAction === "business_advisor") {
              payload = { ...payload, advisor_table_id: destinationTableIds };
            } else if (selectedAction === "unifier_business_advisor") {
              payload = {
                ...payload,
                unifier_table_id: destinationTableIds,
                advisor_table_id: destinationTableIds,
              };
            }
          }
        } else {
          payload = { ...payload, [idKey]: destinationTableIds };
        }
      }
      await dataCollectionBrandsEndPointLogic.brandsRunProcess(
        oauthToken,
        payload,
      );
    } catch (err) {
      console.error("Error running brand process:", err);
      dispatch(setIsSuccess(false));
    } finally {
      dispatch(setIsLoading(false));
      handleClose();
      // Remove this part for https://optival.atlassian.net/browse/GR-20894?focusedCommentId=126063
      // setTimeout(() => {
      //   setActiveStep(0);
      //   setCompleted({});
      //   dispatch(setSelectedAction(null));
      // }, 300);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      maxWidth="md"
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
    >
      <Box>
        <DialogTitle id="alert-dialog-title">{"Update Brand Data"}</DialogTitle>
        <DialogContent>
          <Box sx={{ width: "100%", overflow: "hidden" }}>
            <Stepper nonLinear activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={label} completed={completed[index]}>
                  <StepButton
                    onClick={() => {
                      if (completed[index]) handleStep(index);
                    }}
                    disabled={
                      (index === 1 && shouldSkipProcessSettings) ||
                      !completed[index]
                    }
                    sx={{
                      "& .MuiStepIcon-root": {
                        color:
                          label === "Save" && hasError
                            ? "rgba(211, 47, 47, 1) !important" // Red if there's an error
                            : activeStep === index || completed[index]
                              ? "rgba(156, 39, 176, 1) !important"
                              : "rgba(0, 0, 0, 0.38) !important",
                      },
                      "&.Mui-active .MuiStepIcon-root, &.Mui-completed .MuiStepIcon-root":
                        {
                          color:
                            label === "Save" && hasError
                              ? "rgba(211, 47, 47, 1) !important" // Keep red if error exists
                              : "rgba(156, 39, 176, 1) !important",
                        },
                    }}
                  >
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Box>

          {/* Conditional rendering based on active step */}
          {activeStep === 0 && (
            <Box>
              <ProcessInfo
                onActionSelect={handleActionSelect}
                onDateValidityChange={setIsProcessInfoDateValid}
              />
            </Box>
          )}

          {activeStep === 1 && (
            <Box>
              <ProcessSetting />
            </Box>
          )}

          {activeStep === 2 && (
            <Box>
              <>
                <ReviewAndRun />
              </>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ padding: "0px 24px 20px 24px" }}>
          <Button
            onClick={handleClose} // Function to close the dialog
            color="secondary"
          >
            Cancel
          </Button>
          <Button
            onClick={handleBack}
            variant="outlined"
            color="secondary"
            sx={activeStep === 0 ? { display: "none" } : {}}
            startIcon={<ArrowBackIcon />}
          >
            Back
          </Button>
          <Button
            key={activeStep}
            data-tracker={activeStep === steps.length - 1 ? "360" : undefined}
            onClick={
              activeStep === steps.length - 1 ? handleFinish : handleNext
            }
            color="secondary"
            variant="contained"
            disabled={
              !selectedAction ||
              isLoading ||
              isNextDisabled ||
              (activeStep === 0 && !isProcessInfoDateValid) ||
              isInvalidActionCombination
            }
            endIcon={
              isLoading ? (
                <CircularProgress color="inherit" size={20} />
              ) : activeStep === steps.length - 1 ? (
                <PlayCircleIcon />
              ) : (
                <ArrowForwardOutlinedIcon />
              )
            }
          >
            {isLoading
              ? "Running Process About to Start..."
              : activeStep === steps.length - 1
                ? "Run Process"
                : "Next"}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}
